import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IDropdownOption } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import { IDashboard, IWorkspace } from '../../../dashboards/dashboards.interface';
import { IDataSource } from '../../data-sources/data-sources.interface';
import { IWidgetRequest, IWidgetRequestAddPayload, IWidgetRequestEditPayload } from '../widget-requests.interface';

export interface IWidgetRequestAddEditDialogState {
  dashboardStatus: ELoadStatus;
  dashboards: IDropdownOption[];
  dataSourceStatus: ELoadStatus;
  dataSources: IDropdownOption[];
  workspaceStatus: ELoadStatus;
  workspaces: IDropdownOption[];
  createUpdateStatus: ELoadStatus;
}

@Injectable()
export class WidgetRequestsAddEditDialogStore extends ComponentStore<IWidgetRequestAddEditDialogState> {
  private readonly httpUtilities = inject(HttpUtilitiesService);
  private readonly http = inject(HttpClient);

  public readonly afterCreateUpdateSuccess$ = this.select(({ createUpdateStatus }) => createUpdateStatus).pipe(
    filter((status) => status === ELoadStatus.success),
  );

  public readonly dashboards = this.selectSignal(({ dashboards }) => dashboards);
  public readonly dataSources = this.selectSignal(({ dataSources }) => dataSources);
  public readonly workspaces = this.selectSignal(({ workspaces }) => workspaces);

  public readonly areDataSourcesLoading = this.selectSignal(
    ({ dataSourceStatus }) => dataSourceStatus === ELoadStatus.loading,
  );
  public readonly areWorkspacesLoading = this.selectSignal(
    ({ workspaceStatus }) => workspaceStatus === ELoadStatus.loading,
  );
  public readonly areDashboardsLoading = this.selectSignal(
    ({ dashboardStatus }) => dashboardStatus === ELoadStatus.loading,
  );

  readonly loadDashboards = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dashboardStatus: ELoadStatus.loading });

        return this.getDashboards(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                dashboards: response.data.map(({ id, name }) => ({ id, name })),
                dashboardStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkspaces = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ workspaceStatus: ELoadStatus.loading });

        return this.getWorkspaces(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                workspaces: response.data.map(({ id, name }) => ({ id, name })),
                workspaceStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadDataSources = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataSourceStatus: ELoadStatus.loading });

        return this.getDataSources(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                dataSources: response.data.map(({ id, name }) => ({ id, name })),
                dataSourceStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly createOne = this.effect((trigger$: Observable<IWidgetRequestAddPayload>) =>
    trigger$.pipe(
      switchMap((item: IWidgetRequestAddPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ createUpdateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error creating data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IWidgetRequestEditPayload>) =>
    trigger$.pipe(
      switchMap((item: IWidgetRequestEditPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ createUpdateStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error updating data', error),
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      createUpdateStatus: ELoadStatus.initial,
      dashboards: [],
      dashboardStatus: ELoadStatus.initial,
      dataSources: [],
      dataSourceStatus: ELoadStatus.initial,
      workspaces: [],
      workspaceStatus: ELoadStatus.initial,
    });
  }

  private createItem(body: IWidgetRequestAddPayload): Observable<IBaseResponse<IWidgetRequest>> {
    return this.http.post<IBaseResponse<IWidgetRequest>>('widget-requests', body);
  }

  private updateItem(edit: IWidgetRequestEditPayload): Observable<IBaseResponse<IWidgetRequest>> {
    return this.http.patch<IBaseResponse<IWidgetRequest>>(`widget-requests/${edit.id}`, edit.dto);
  }

  private getDashboards(params: HttpParams): Observable<IGetManyResponse<IDashboard>> {
    return this.http.get<IGetManyResponse<IDashboard>>('dashboards', { params });
  }

  private getWorkspaces(params: HttpParams): Observable<IGetManyResponse<IWorkspace>> {
    return this.http.get<IGetManyResponse<IWorkspace>>('workspaces', { params });
  }

  private getDataSources(params: HttpParams): Observable<IGetManyResponse<IDataSource>> {
    return this.http.get<IGetManyResponse<IDataSource>>('data-sources', { params });
  }
}
